import { useState, type ReactElement } from 'react';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/useToast';

import {
  readLaunchJobId,
  useDeployTenant,
  useEditOverrides,
  useStopTenant,
  useUpdateTenantDeployment,
  type ServiceOverrideInput,
  type ServiceOverrideViewModel,
  type TenantViewModel,
} from '../api';
import { OVERRIDABLE_SERVICES, type OverridableServiceName } from '../endpoints';
import { isNonTerminalDeploymentStatus } from '../overrides';
import { DeploymentStatusPill } from './DeploymentStatusPill';
import { JobProgressIndicator } from './JobProgressIndicator';

function shortDigest(digest: string | null): string | null {
  if (digest === null) return null;
  // `sha256:abcdef…` → keep the algorithm prefix + first 8 hex chars.
  const colon = digest.indexOf(':');
  const algo = colon === -1 ? '' : digest.slice(0, colon);
  const hex = colon === -1 ? digest : digest.slice(colon + 1);
  const head = hex.slice(0, 8);
  return algo.length > 0 ? `${algo}:${head}…` : `${head}…`;
}

/** Build the full override list to PUT, applying an edit to one service. */
function withEditedTag(
  overrides: readonly ServiceOverrideViewModel[],
  service: OverridableServiceName,
  newTag: string,
): ServiceOverrideInput[] {
  return overrides.map((o) =>
    o.serviceName === service
      ? { serviceName: o.serviceName, imageTag: newTag.trim() }
      : { serviceName: o.serviceName, imageTag: o.imageTag },
  );
}

export function OverridesPanel({
  tenant,
  canManage,
}: {
  readonly tenant: TenantViewModel;
  readonly canManage: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const deploy = useDeployTenant();
  const update = useUpdateTenantDeployment();
  const stop = useStopTenant();
  const editOverrides = useEditOverrides();

  const [actionJobId, setActionJobId] = useState<number | null>(null);
  const [editingService, setEditingService] = useState<OverridableServiceName | null>(null);
  const [editTag, setEditTag] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [redeployHint, setRedeployHint] = useState(false);

  // A tenant is "busy" while its deployment status is non-terminal (mid job).
  const busy = isNonTerminalDeploymentStatus(tenant.deploymentStatus);

  const overrideByService = new Map<OverridableServiceName, ServiceOverrideViewModel>(
    tenant.serviceOverrides.map((o) => [o.serviceName, o]),
  );

  function runAction(
    mutation: typeof deploy,
    successKey: string,
  ): void {
    mutation.mutate(tenant.uuid, {
      onSuccess: (launch) => {
        // `DeploymentActionResponse` is `@JsonInclude(NON_NULL)`; a missing id
        // would otherwise track a never-terminal job. Fail loudly. (CR6)
        const jobId = readLaunchJobId(launch);
        if (jobId === null) {
          toast({
            title: t('routes.platform.detail.overrides.noJob'),
            variant: 'destructive',
          });
          return;
        }
        setActionJobId(jobId);
        toast({ title: t(successKey, { id: jobId }), variant: 'success' });
      },
      onError: (error) => {
        toast({
          title: t('routes.platform.detail.overrides.actionError', { code: getErrorCodeForUi(error) }),
          variant: 'destructive',
        });
      },
    });
  }

  function startEdit(service: OverridableServiceName, currentTag: string): void {
    setEditingService(service);
    setEditTag(currentTag);
    setEditError(null);
  }

  function cancelEdit(): void {
    setEditingService(null);
    setEditTag('');
    setEditError(null);
  }

  function saveEdit(service: OverridableServiceName): void {
    if (editTag.trim().length === 0) {
      setEditError(t('routes.platform.detail.overrides.tagRequired'));
      return;
    }
    editOverrides.mutate(
      { uuid: tenant.uuid, serviceOverrides: withEditedTag(tenant.serviceOverrides, service, editTag) },
      {
        onSuccess: () => {
          cancelEdit();
          setRedeployHint(true);
          toast({ title: t('routes.platform.detail.overrides.editSaved'), variant: 'success' });
        },
        onError: (error) => {
          toast({
            title: t('routes.platform.detail.overrides.editError', { code: getErrorCodeForUi(error) }),
            variant: 'destructive',
          });
        },
      },
    );
  }

  return (
    <section aria-label={t('routes.platform.detail.overrides.heading')} className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold">{t('routes.platform.detail.overrides.heading')}</h2>
          <DeploymentStatusPill status={tenant.deploymentStatus} />
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" disabled={busy} onClick={() => { runAction(deploy, 'routes.platform.detail.overrides.deployLaunched'); }}>
              {t('routes.platform.detail.overrides.deploy')}
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => { runAction(update, 'routes.platform.detail.overrides.updateLaunched'); }}>
              {t('routes.platform.detail.overrides.update')}
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => { runAction(stop, 'routes.platform.detail.overrides.stopLaunched'); }}>
              {t('routes.platform.detail.overrides.stop')}
            </Button>
          </div>
        )}
      </div>

      {busy && (
        <p className="text-xs text-muted-foreground" role="status">
          {t('routes.platform.detail.overrides.busy')}
        </p>
      )}
      {redeployHint && (
        <p className="text-xs text-amber-700" role="status">
          {t('routes.platform.detail.overrides.redeployHint')}
        </p>
      )}
      {actionJobId !== null && <JobProgressIndicator jobExecutionId={actionJobId} />}

      <DataTableTable aria-label={t('routes.platform.detail.overrides.heading')}>
        <DataTableThead>
          <DataTableHeaderRow>
            <DataTableTh widthClassName="w-[20%]">{t('routes.platform.detail.overrides.table.service')}</DataTableTh>
            <DataTableTh widthClassName="w-[35%]">{t('routes.platform.detail.overrides.table.tag')}</DataTableTh>
            <DataTableTh widthClassName="w-[25%]">{t('routes.platform.detail.overrides.table.digest')}</DataTableTh>
            <DataTableTh widthClassName="w-[20%]" align="right">
              <span className="sr-only">{t('routes.platform.detail.overrides.table.actions')}</span>
            </DataTableTh>
          </DataTableHeaderRow>
        </DataTableThead>
        <DataTableTbody>
          {OVERRIDABLE_SERVICES.map((service) => {
            const override = overrideByService.get(service) ?? null;
            const isEditing = editingService === service;
            const digest = override !== null ? shortDigest(override.imageDigestAtDeploy) : null;
            return (
              <DataTableTr key={service}>
                <DataTableTd className="font-mono text-[12px]">{service}</DataTableTd>
                <DataTableTd dataOffset>
                  {override === null ? (
                    <span className="text-muted-foreground">
                      {t('routes.platform.detail.overrides.notOverridden')}
                    </span>
                  ) : isEditing ? (
                    <div className="space-y-1">
                      <Input
                        aria-label={t('routes.platform.detail.overrides.editTag')}
                        value={editTag}
                        onChange={(e) => { setEditTag(e.target.value); }}
                        autoComplete="off"
                        spellCheck={false}
                        className="font-mono"
                      />
                      {editError !== null && (
                        <p role="alert" className="text-xs text-red-600">{editError}</p>
                      )}
                    </div>
                  ) : (
                    <span className="font-mono text-[12px]">{override.imageTag}</span>
                  )}
                </DataTableTd>
                <DataTableTd dataOffset className="font-mono text-[11px] text-muted-foreground">
                  {digest ?? '—'}
                </DataTableTd>
                <DataTableTd align="right" dataOffset>
                  {override !== null && canManage && (
                    isEditing ? (
                      <span className="inline-flex gap-1">
                        <Button
                          type="button"
                          size="sm"
                          className="h-7 px-2 text-[11px]"
                          loading={editOverrides.isPending}
                          onClick={() => { saveEdit(service); }}
                        >
                          {t('routes.platform.detail.overrides.saveTag')}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[11px]"
                          disabled={editOverrides.isPending}
                          onClick={cancelEdit}
                        >
                          {t('routes.platform.detail.overrides.cancelTag')}
                        </Button>
                      </span>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[11px]"
                        disabled={busy}
                        onClick={() => { startEdit(service, override.imageTag); }}
                      >
                        <Pencil className="mr-1 h-3 w-3" aria-hidden="true" />
                        {t('routes.platform.detail.overrides.editTag')}
                      </Button>
                    )
                  )}
                </DataTableTd>
              </DataTableTr>
            );
          })}
        </DataTableTbody>
      </DataTableTable>
    </section>
  );
}
